import fileUpload from '@connect-soft/upload-file-lib';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
    cloneElement,
    type ReactElement,
    ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import FileUploadProgress from './FileUploadProgress';

export type ICallable = {
    sendFiles: () => void;
};
type ISlots = {
    input: ReactElement;
    submit: ReactElement;
    dialogButton: ReactElement;
};

export type IProps = {
    additionalData?: Record<string, string>;
    apiPath: string;
    config?: Omit<DropzoneOptions, 'disabled' | 'noClick'>;
    disabled?: boolean;
    innerRef?: Ref<ICallable>;
    labelSubmit?: ReactNode;
    labelDialogButton?: ReactNode;
    maxUploadedSize?: number;
    requestHeaders?: Record<string, string>;
    slots?: Partial<ISlots>;
    titleDropZone?: ReactNode;
    titleFiles?: ReactNode;
    withButton?: boolean;
    withDropZone?: boolean;
    onFinished?: (status: 'success' | 'error', uploadedFiles: IFile[]) => void;
    onStart?: () => void;
};

const InputField = styled.input`
    width: 350px;
    max-width: 100%;
    color: #444;
    padding: 5px;
    background: #fff;
    border-radius: 10px;
    border: 1px solid #555;

    &::file-selector-button {
        margin-right: 20px;
        border: none;
        background-color: ${(props) => (props.disabled ? 'gray' : '#04aa6d')};
        pointer-events: ${(props) => (props.disabled ? 'none' : null)};
        padding: 10px 20px;
        border-radius: 10px;
        color: #fff;
        cursor: pointer;
        transition: background 0.2s ease-in-out;
    }
`;
const DialogButton = styled.button`
    background: none;
    color: black;
    border: none;
    border-radius: 25px;
    font-size: 16px;
    height: 50px;
    width: 50px;
    cursor: pointer;
    transition: 0.3s;

    &:hover {
        background-color: DodgerBlue;
        color: white;
    }
`;
const Button = styled.button`
    background-color: ${(props) => (props.disabled ? 'gray' : '#04aa6d')};
    pointer-events: ${(props) => (props.disabled ? 'none' : null)};
    border-radius: 10px;
    border: none;
    color: white;
    padding: 16px 32px;
    text-decoration: none;
    margin: 4px 2px;
    cursor: pointer;
`;
const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;
const StyledDropZone = styled.div`
    width: 100%;
    padding: 20px;
    border-width: 2px;
    border-radius: 2px;
    border-color: #eeeeee;
    border-style: dashed;
    background-color: #fafafa;
    color: #bdbdbd;
    outline: none;
    transition: border 0.24s ease-in-out;
`;
const StyledFileListWrapper = styled.aside`
    width: 100%;
    text-align: left;
`;

type IFile = {
    content: File;
    uid: string;
    percentage: number;
    response?: string;
    status: 'no' | 'success' | 'error';
};

const FileUpload = ({
    additionalData = {},
    apiPath,
    config,
    disabled = false,
    innerRef,
    labelDialogButton = <CloudUploadIcon fontSize="large" />,
    labelSubmit = 'Upload',
    maxUploadedSize = 200000,
    requestHeaders,
    slots = {},
    titleDropZone = <Typography variant="h3">Drop Zone</Typography>,
    titleFiles = <Typography variant="h4">Files</Typography>,
    withButton = true,
    withDropZone = false,
    onFinished = () => {},
    onStart = () => {}
}: IProps) => {
    const { getRootProps, getInputProps, acceptedFiles, open } = useDropzone({
        ...config,
        noClick: true,
        disabled
    });

    const [uploadingInProgress, setUploadingInProgress] = useState(false);
    const [allUploaded, setAllUploaded] = useState(false);

    const {
        input: inputControl = <InputField />,
        submit: submitButton = <Button />,
        dialogButton: openDialogButton = <DialogButton />
    } = slots;
    const inputParentProps = slots?.input?.props ?? {};

    const [files, setFiles] = useState<IFile[] | null>(null);
    const { style: inputStyles, ...inputProps } = getInputProps();

    useEffect(() => {
        const validAcceptedFiles = acceptedFiles
            .map(
                (item) =>
                    files?.find((file) => file.content === item) ??
                    ({
                        content: item,
                        uid: uuidv4(),
                        percentage: 0,
                        status: 'no'
                    } as IFile)
            )
            .filter((item) => !files?.some((file) => file.uid === item.uid));

        if (validAcceptedFiles.length) {
            setFiles([...(files ?? []), ...validAcceptedFiles]);
        }
    }, [acceptedFiles, files]);
    useEffect(() => {
        const isAllUploaded = !files?.some((file) => file.status === 'no');

        if (isAllUploaded !== allUploaded && uploadingInProgress) {
            setAllUploaded(isAllUploaded);
            setUploadingInProgress(false);
        }
    }, [allUploaded, files, uploadingInProgress]);
    useEffect(() => {
        if (allUploaded && onFinished) {
            onFinished(files?.some(({ status }) => status === 'error') ? 'error' : 'success', files ?? []);
        }
    }, [allUploaded, files, onFinished]);

    const handleUpload = useCallback(() => {
        setUploadingInProgress(true);
        setAllUploaded(false);
        files
            ?.filter((file) => file.percentage !== 1 && file.status !== 'success')
            .forEach((file) => {
                fileUpload(
                    apiPath,
                    file.uid,
                    file.content,
                    maxUploadedSize ?? 200000,
                    (percentage) => {
                        setFiles(
                            (currentFiles) =>
                                currentFiles?.map((item) => {
                                    if (item.uid === file.uid) {
                                        return {
                                            ...item,
                                            percentage
                                        } as IFile;
                                    } else {
                                        return item;
                                    }
                                }) ?? null
                        );
                    },
                    requestHeaders,
                    additionalData
                )
                    .then((data) => {
                        const { status, response } = data;

                        setFiles(
                            (currentFiles) =>
                                currentFiles?.map((item) => {
                                    if (item.uid === file.uid) {
                                        return {
                                            ...item,
                                            percentage: 1,
                                            response,
                                            status: status === 'success' ? 'success' : 'error'
                                        } as IFile;
                                    } else {
                                        return item;
                                    }
                                }) ?? null
                        );
                    })
                    .catch(() => {
                        setFiles(
                            (currentFiles) =>
                                currentFiles?.map((item) => {
                                    if (item.uid === file.uid) {
                                        return {
                                            ...item,
                                            status: 'error'
                                        } as IFile;
                                    } else {
                                        return item;
                                    }
                                }) ?? null
                        );
                    });
            });
    }, [additionalData, apiPath, files, maxUploadedSize, requestHeaders]);

    useImperativeHandle(
        innerRef,
        () => ({
            sendFiles: () => {
                if (onStart) {
                    onStart();
                }

                handleUpload();
            }
        }),
        [handleUpload, onStart]
    );

    return (
        <Box sx={{ textAlign: 'center' }}>
            {withDropZone ? (
                <Container>
                    <StyledDropZone {...getRootProps({ className: 'dropzone' })}>
                        <input {...inputProps} style={inputStyles} />
                        {titleDropZone}
                        {openDialogButton ? (
                            cloneElement(openDialogButton, {
                                disabled: disabled,
                                onClick: open,
                                children: labelDialogButton
                            })
                        ) : (
                            <button disabled={disabled} onClick={handleUpload}>
                                {labelDialogButton}
                            </button>
                        )}
                    </StyledDropZone>
                    <StyledFileListWrapper>
                        {titleFiles}
                        <Grid container rowSpacing={1} direction="row">
                            {files?.map((file) => (
                                <Grid item key={file.uid} sx={{ width: { xs: '250px', md: '500px' } }}>
                                    {file.content.name}
                                    <FileUploadProgress value={file.percentage * 100} />
                                </Grid>
                            ))}
                        </Grid>
                    </StyledFileListWrapper>
                </Container>
            ) : (
                <>
                    {inputControl ? (
                        cloneElement(inputControl, {
                            ...inputParentProps,
                            ...inputProps,
                            disabled
                        })
                    ) : (
                        <input {...inputProps} disabled={disabled} />
                    )}
                </>
            )}
            {withButton && (
                <>
                    {submitButton ? (
                        cloneElement(submitButton, {
                            disabled: disabled,
                            onClick: handleUpload,
                            children: labelSubmit
                        })
                    ) : (
                        <button disabled={disabled} onClick={handleUpload} type="button">
                            {labelSubmit}
                        </button>
                    )}
                </>
            )}
        </Box>
    );
};

export default FileUpload;
